import * as React from 'react';
import {
  Grid,
  Card,
  CardHeader,
  CardContent,
  Box,
  Paper,
  Divider,
  Button,
  Typography,
  Icon,
  CircularProgress,
  IconButton,
} from '@material-ui/core';
import AddCircleIcon from '@material-ui/icons/AddCircle';
import {
  Edit,
} from '@material-ui/icons';
import {
  makeStyles, createStyles,
} from '@material-ui/core/styles';
import { useSnackbar } from 'notistack';
import CompanyDetails from './CompanyDetails';
import CompanyOverview from './CompanyOverview';
import type ICompanyContactDetails from './CompanyInterfaces';
import { colors } from '../../../theme';
import CompanyTags from './CompanyTags';

const useStyles = makeStyles(() => createStyles({
  backgroundBlue: {
    background: colors.primary,
  },
  textBlue: {
    color: colors.primary,
  },
  filterBtn: {
    background: colors.primary,
    color: colors.white,
  },
}));

interface IEditCompanyProps {
  editingHidden: boolean;
  isFetching?: boolean;
  backToAllCompanies?: () => void;
  handleSaveBtnClicked?: () => void;
  setCompanyFormValues: (values: ICompanyContactDetails) => void;
  companyFormValues: ICompanyContactDetails;
}

const EditCompany: React.FC<IEditCompanyProps> = ({
  editingHidden, isFetching, backToAllCompanies, handleSaveBtnClicked, setCompanyFormValues, companyFormValues,
}) => {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();

  const handleAddNewTag = () => {
    if (companyFormValues.tags.some((tag) => (tag.key === '' || tag.value === '') && !(tag.inheritable))) {
      enqueueSnackbar('Cannot add new tag, while previous tags are not filled out', { variant: 'error' });
    } else {
      const temp = [...companyFormValues.tags];
      temp.push({
        inheritable: false,
        key: '',
        value: '',
      });
      setCompanyFormValues({ ...companyFormValues, tags: temp });
    }
  };

  const handleRemoveTag = (index: number) => {
    const temp = [...companyFormValues.tags];
    temp.splice(index, 1);
    setCompanyFormValues({ ...companyFormValues, tags: temp });
  };

  return (
    <>
      <Grid container spacing={0}>
        <Grid item xs={12}>
          <Box py={1}>
            {/* Hide if on add company page. */}
            {!editingHidden && (
              <>
                <Grid container xs={4}>
                  <Grid item>
                    <Box p={1}>
                      <Icon>
                        <Edit
                          color="primary"
                        />
                      </Icon>
                    </Box>
                  </Grid>
                  <Grid item>
                    <Box p={1}>
                      <Typography variant="h6" className={classes.textBlue} display="inline">{'Edit Company'}</Typography>
                    </Box>
                  </Grid>
                </Grid>
              </>
            )}
          </Box>

          <Box py={3}>
            <Card>
              <Paper>
                <CardHeader title="Company Overview" />
                <Divider variant="fullWidth" />
                <CardContent>
                  <CompanyOverview
                    formValues={companyFormValues}
                    setFormValues={setCompanyFormValues}
                  />
                </CardContent>
              </Paper>
            </Card>
          </Box>
          <Box>
            <Card>
              <Paper>
                <CardHeader title="Assign Tags" />
                <Divider variant="fullWidth" />
                <CardContent>
                  <CompanyTags
                    formValues={companyFormValues}
                    setFormValues={setCompanyFormValues}
                    handleRemove={handleRemoveTag}
                  />
                  <Grid item xs={12}>
                    <Box px={1} clone>
                      <Button
                        variant="text"
                        color="primary"
                        onClick={() => { handleAddNewTag(); }}
                        style={{ minWidth: '5.625rem' }}
                      >
                        <IconButton disableFocusRipple disabled disableRipple>
                          <AddCircleIcon style={{ color: '#6CAE75', float: 'right' }} fontSize="large" />
                        </IconButton>
                        {'ADD TAG'}
                      </Button>
                    </Box>
                  </Grid>
                </CardContent>
              </Paper>
            </Card>
          </Box>
          <Box py={3}>
            <Card>
              <Paper>
                <CardHeader title="Company Details" />
                <Divider variant="fullWidth" />
                <CardContent>
                  <CompanyDetails
                    formValues={companyFormValues}
                    setFormValues={setCompanyFormValues}
                  />
                </CardContent>
              </Paper>
            </Card>
            <Grid item xs={12} container spacing={2} direction="row-reverse">
              <Box pt={3}>
                <Button onClick={backToAllCompanies}>
                  <Typography className={classes.textBlue} display="inline">{'BACK TO ALL COMPANIES'}</Typography>
                </Button>
                <Button disabled={isFetching} color="primary" variant="contained" onClick={handleSaveBtnClicked}>
                  {isFetching ? <CircularProgress size={25} />
                    : 'SAVE CHANGES'}
                </Button>
              </Box>
            </Grid>
          </Box>
        </Grid>
      </Grid>
    </>
  );
};

export default EditCompany;
